import { PapiExtendedGroup, PapiUser } from '@wix/da-papi-types';
import { createSelector } from 'reselect';
import {
  getCurrentUser,
  getCurrentUserHasCore,
  getPagingMode,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import { getUserById } from '@wix/da-shared-react/pkg/redux/users/selectors';
import { AppState } from '../../types/store';

import { PagePriv } from '../../types/pagePrivileges';
import { canPerformPageAction } from './groups';

export const getProfileOwner = (
  state: AppState
): Exclude<AppState['profileOwner'], 'user'> & { user: PapiUser } => ({
  ...state.profileOwner,
  user: getUserById(state, state.profileOwner.user.id),
});

export const getCanEditPage = (state): boolean =>
  isCurrentUserProfileOwner(state) ||
  canPerformPageAction(state, PagePriv.ADD_MODULE);

export const getIsGroupProfile = (state: AppState) =>
  state.profileOwner.isGroup;

export const getIsWatchingProfileOwner = (state: AppState) =>
  state.profileOwner.isWatching;

export const getProfileOwnerIsWatcher = (state: AppState) =>
  state.profileOwner.isWatcher;

export const getProfileOwnerMonetizationPromo = (state: AppState) =>
  state.profileOwner.showMonetizationPromo;

export const getProfileOwnerStats = (state: AppState) =>
  state.profileOwner.stats;

export const getProfileOwnerStat = (state: AppState, entity) =>
  getProfileOwnerStats(state)[entity];

export const getProfileOwnerTagline = (state: AppState) =>
  state.profileOwner.tagline;

export const getProfileOwnerUser = (state: AppState): PapiUser =>
  getUserById(state, state.profileOwner.user.id);

export const getProfileOwnerExtendedGroup = (
  state: AppState
): PapiExtendedGroup | undefined => state.profileOwner.extendedGroup;

export const isLoggedIn = state => getCurrentUser(state) !== undefined;

export const isCurrentUserProfileOwner = (state: AppState): boolean => {
  const currentUser = getCurrentUser(state);
  const profileOwner = getProfileOwnerUser(state);
  // We expected to use PagePriv.EDIT_PAGE here, but edit page privilege is
  // unused on green site, so we will use the ADD_MODULE priv for now to detect
  // wether the user has ownership over the page
  const canEditPage = canPerformPageAction(state, PagePriv.ADD_MODULE);
  return (
    !!currentUser && (profileOwner.userId === currentUser.userId || canEditPage)
  );
};

export const getShowCoreUpsell = createSelector(
  [isCurrentUserProfileOwner, getCurrentUserHasCore, getIsGroupProfile],
  (isOwner, hasCore, isGroup) => isOwner && !hasCore && !isGroup
);

export const getIsPagedMode = (state: AppState) =>
  getPagingMode(state) === 'page';
